import React, { useMemo, useRef, useEffect, useState } from "react";
import { X, Search, User } from "lucide-react";
import AddPatientModal from "./AddPatientModal";
import { useLazySearchPatientQuery } from "../../State/Api/PharmacySaleApiSlice"; 

const formatINR = (n) =>
  (n ?? 0).toLocaleString("en-IN", { style: "currency", currency: "INR" });

export default function PatientSelectionModal({
  open,
  onClose,
  onSelect,
  data = [],
  formik
}) {
  const [searchText, setSearchText] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const [showAdd, setShowAdd] = useState(false);
  const [rows, setRows] = useState([]); 
  const inputRef = useRef(null);

  const [searchPatient, { data : results}] = useLazySearchPatientQuery();

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 0);
  }, [open]);

useEffect(() => {
  if (!open) return;                          // do nothing if modal closed
  const q = searchText.trim();
  if (q === "") {
    return;
  }
  if (document.activeElement !== inputRef.current) return; // only when user is typing in the box

  const timeout = setTimeout(async () => {
    try {
      const result = await searchPatient({ 
        pharmaId: localStorage.getItem("userUniqueID"), 
        patientName: q 
      }).unwrap();
      setRows(result);
    } catch (err) {
      if (err.name !== "AbortError") console.error(err);
    }
  }, 300);

  return () => {                              // cancel in-flight if user keeps typing
    clearTimeout(timeout);
  };
}, [searchText, searchPatient]);  


  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((a) => Math.min(a + 1, Math.max(results.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter" && results[activeIdx]) {
      e.preventDefault();
      onSelect(results[activeIdx]);
      onClose();
    }
  };

  if (!open) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: "12px",
          width: "90%",
          maxWidth: "700px",
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
      {/* Header with Create New and Close */}
<div
  style={{
    padding: "24px",
    borderBottom: "2px solid #e5e7eb",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  }}
>
  <div>
    <h2
      style={{
        fontSize: "18px",
        fontWeight: 700,
        margin: 0,
        color: "#1f2937",
      }}
    >
      Select a Patient
    </h2>
    <p
      style={{
        fontSize: "13px",
        color: "#999",
        margin: "4px 0 0 0",
      }}
    >
      Select an option from the below list
    </p>
  </div>

  {/* Create New + Close buttons */}
  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
    <button
      onClick={() => setShowAdd(true)} // will open AddPatientModal
      className="gradient-button"
    //   style={{
    //     background: "#111827",
    //     color: "#fff",
    //     border: "none",
    //     borderRadius: "8px",
    //     padding: "8px 16px",
    //     fontWeight: 600,
    //     cursor: "pointer",
    //   }}
    >
      Create New
    </button>
    <button
      onClick={onClose}
      style={{
        background: "#fee2e2",
        border: "none",
        borderRadius: "6px",
        width: "36px",
        height: "36px",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <X size={18} color="#dc2626" />
    </button>
  </div>
</div>

        {/* Search */}
        <div style={{ padding: "16px 24px", borderBottom: "1px solid #e5e7eb" }}>
          <div
            style={{
              fontSize: "12px",
              fontWeight: 600,
              color: "#374151",
              marginBottom: "8px",
            }}
          >
            SEARCH PATIENT
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              position: "relative",
            }}
          >
            <Search
              size={16}
              color="#999"
              style={{ position: "absolute", left: "12px" }}
            />
            <input
              ref={inputRef}
              placeholder="Search by Patient Name or Mobile"
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setActiveIdx(0);
              }}
              onKeyDown={onKeyDown}
              style={{
                flex: 1,
                padding: "10px 12px 10px 40px",
                border: "2px solid #4a8e6fff",
                borderRadius: "8px",
                fontSize: "14px",
                outline: "none",
              }}
            />
          </div>
        </div>

        {/* Table */}
        <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
          {results?.length === 0 ? (
            <div
              style={{
                padding: "48px 24px",
                textAlign: "center",
                color: "#999",
              }}
            >
              <User
                size={40}
                color="#d1d5db"
                style={{ marginBottom: "12px" }}
              />
              <p style={{ margin: 0, fontSize: "14px" }}>No patients found</p>
            </div>
          ) : (
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "13px",
              }}
            >
              <thead style={{ position: "sticky", top: 0 }}>
                <tr
                  style={{
                    background: "#f9fafb",
                    borderBottom: "2px solid #e5e7eb",
                  }}
                >
                  <th
                    style={{
                      padding: "12px 16px",
                      textAlign: "left",
                      fontWeight: 700,
                      color: "#374151",
                    }}
                  >
                    PATIENT’S NAME / ADDRESS
                  </th>
                  <th
                    style={{
                      padding: "12px 16px",
                      textAlign: "center",
                      fontWeight: 700,
                      color: "#374151",
                      minWidth: "100px",
                    }}
                  >
                    Gender
                  </th>
                  <th
                    style={{
                      padding: "12px 16px",
                      textAlign: "center",
                      fontWeight: 700,
                      color: "#374151",
                      minWidth: "100px",
                    }}
                  >
                    MOBILE NO
                  </th>
                  <th
                style={{
                    padding: "12px 16px",
                    textAlign: "right",
                    fontWeight: 700,
                    color: "#374151",
                    minWidth: "100px",
                }}
                >
                DISCOUNT
                </th>

                </tr>
              </thead>
              <tbody>
                {results?.map((patient, idx) => (
                  <tr
                    key={patient.id}
                    onClick={() => {
                      onSelect(patient);
                      formik?.setFieldValue("patientName", patient.patientName);
                      onClose();
                    }}
                    style={{
                      background:
                        idx === activeIdx
                          ? "#f0fdf4"
                          : idx % 2 === 0
                          ? "#fff"
                          : "#fafbfc",
                      borderBottom: "1px solid #e5e7eb",
                      cursor: "pointer",
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "#f0fdf4")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background =
                        idx === activeIdx
                          ? "#f0fdf4"
                          : idx % 2 === 0
                          ? "#fff"
                          : "#fafbfc")
                    }
                  >
                    <td style={{ padding: "12px 16px" }}>
                      <div style={{ fontWeight: 600, color: "#1f2937" }}>
                        {patient.patientName}
                      </div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#999",
                          marginTop: "2px",
                        }}
                      >
                        {patient.address}
                      </div>
                    </td>
                    <td
                      style={{
                        padding: "12px 16px",
                        textAlign: "center",
                        color: "#1f2937",
                      }}
                    >
                      {patient.gender || "-"}
                    </td>
                   <td
  style={{
    padding: "12px 16px",
    textAlign: "center",
    color: "#1f2937",
  }}
>
  {patient.mobileNumber || "-"}
</td>

<td
  style={{
    padding: "12px 16px",
    textAlign: "right",
    color: patient.discount > 0 ? "#059669" : "#6b7280",
    fontWeight: 600,
  }}
>
  {patient.salesDiscount > 0 ? `${patient.salesDiscount}%` : "-"}
</td>

                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>


        <AddPatientModal
  open={showAdd}
  onClose={() => setShowAdd(false)}
  onSave={(newPatient) => {
    setRows((prev) => [newPatient, ...prev]); // add new one to list
    onSelect(newPatient);
    setShowAdd(false);
    onClose();
  }}
/>

      </div>
    </div>
  );
}
